import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import BookmarkButton from "../components/workout/BookmarkButton";
import PlaceholderImage from "../components/PlaceholderImage";
import { saveExercise, unsaveExercise } from "../utils/saveExercise";
import { getUserExercises } from "../utils/saveWorkout";
import exercises from "../data/exercises.json";
import ProgressionSection from "../components/progressions/ProgressionSection";

function buildFilterLabel(search, groups, category, level, prefix = "All") {
  const parts = [];
  if (level) parts.push(level.charAt(0).toUpperCase() + level.slice(1));
  if (groups && groups.length > 0) {
    const groupLabels = groups.map(group => group.charAt(0).toUpperCase() + group.slice(1));
    parts.push(groupLabels.join(", "));
  }
  if (category) parts.push(category.charAt(0).toUpperCase() + category.slice(1));
  if (search) parts.push(`"${search}"`);

  if (parts.length === 0) {
    return `${prefix} Exercises`;
  }
  return `${prefix} ${parts.join(" ")} Exercises`;
}

export default function ExerciseDetail({ user }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [savedExerciseIds, setSavedExerciseIds] = useState([]);

  // Check if user came from saved exercises
  const fromSaved = searchParams.get('from') === 'saved';

  // Restore filters from localStorage (same keys used in ExplorePage)
  const savedSearch = localStorage.getItem("exploreSearch") || "";
  const savedGroups = JSON.parse(localStorage.getItem("explorefilterGroups") || "[]");
  const savedCategory = localStorage.getItem("exploreFilterCategory") || "";
  const savedLevel = localStorage.getItem("exploreFilterLevel") || "";

  // Build dynamic labels based on source
  const backLabel = fromSaved 
    ? "← Back to Saved Exercises"
    : `← Back to ${buildFilterLabel(savedSearch, savedGroups, savedCategory, savedLevel)}`;
  const moreLabel = buildFilterLabel(savedSearch, savedGroups, savedCategory, savedLevel, "More");


  // Find the exercise by id
  const exercise = exercises.find((ex) => ex.id === id);

  // Scroll to top when exercise changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  // Crossfade animation for images
  useEffect(() => {
    if (!exercise?.images || exercise.images.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % exercise.images.length);
    }, 3000); // Change every 3 seconds

    return () => clearInterval(interval);
  }, [exercise?.images]);

  // Fetch user's saved exercises
  useEffect(() => {
    const fetchSavedExercises = async () => {
      if (!user) return;
      
      try {
        const savedExercises = await getUserExercises(user);
        const ids = savedExercises.map(ex => ex.id);
        setSavedExerciseIds(ids);
      } catch (error) {
        console.error("Error fetching saved exercises:", error);
      }
    };

    fetchSavedExercises();
  }, [user]);

  // Check if exercise is bookmarked and handle post-login bookmark activation
  useEffect(() => {
    if (user && exercise) {
      // Check if there's a pending bookmark for this exercise
      const pendingBookmark = localStorage.getItem("pendingBookmark");
      if (pendingBookmark) {
        const { exerciseId, returnUrl } = JSON.parse(pendingBookmark);
        if (exerciseId === exercise.id && returnUrl === `/exercise/${id}`) {
          // This is the exercise that was bookmarked before login
          handlePostLoginBookmark();
          localStorage.removeItem("pendingBookmark");
        }
      }
      
      // Check if exercise is in user's saved exercises
      setIsBookmarked(savedExerciseIds.includes(exercise.id));
    }
  }, [user, exercise, id, savedExerciseIds]);

  const handlePostLoginBookmark = async () => {
    try {
      const savedId = await saveExercise(user, exercise);
      if (savedId) {
        setIsBookmarked(true);
        toast.success(`${exercise.name} saved to your library!`);
      } else {
        toast.info(`${exercise.name} is already saved.`);
      }
    } catch (err) {
      console.error("Error saving exercise after login:", err);
      toast.error("Something went wrong.");
    }
  };

  const handleBookmark = async () => {
    if (!user) {
      // Store pending bookmark info and redirect to login
      const pendingBookmarkData = {
        exerciseId: exercise.id,
        returnUrl: `/exercise/${id}`,
        exerciseName: exercise.name
      };
      localStorage.setItem("pendingBookmark", JSON.stringify(pendingBookmarkData));
      navigate("/login");
      return;
    }

    try {
      if (isBookmarked) {
        await unsaveExercise(user, exercise.id);
        setIsBookmarked(false);
        setSavedExerciseIds(prev => prev.filter(id => id !== exercise.id));
        toast.info(`${exercise.name} removed from your library.`);
      } else {
        const savedId = await saveExercise(user, exercise);
        if (savedId) {
          setIsBookmarked(true);
          setSavedExerciseIds(prev => [...prev, exercise.id]);
          toast.success(`${exercise.name} saved to your library!`);
        } else {
          toast.info(`${exercise.name} is already saved.`);
        }
      }
    } catch (err) {
      console.error("Error toggling save state:", err);
      toast.error("Something went wrong.");
    }
  };

  const handleBackToResults = () => {
    // Navigate back based on source
    if (fromSaved) {
      navigate("/library");
    } else {
      // Go back to explore page with preserved filters
      navigate("/explore");
    }
  };

  if (!exercise) {
    return <div className="p-6">Exercise not found.</div>;
  }

  // Get related exercises for "More Results" section
  const getRelatedExercises = () => {
    const related = exercises
      .filter(ex => ex.id !== exercise.id)
      .slice(0, 6); // Show 6 related exercises
    return related;
  };

  const relatedExercises = getRelatedExercises();

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-4xl mx-auto p-6">
        {/* Back button */}
        <button
          onClick={handleBackToResults}
          className="mb-6 text-blue-600 hover:underline font-medium"
        >
          {backLabel}
        </button>


        {/* Exercise Title and Bookmark Row */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 mb-3">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 flex-1">{exercise.name}</h1>
          <div className="flex justify-end sm:justify-start">
            <BookmarkButton
              isSaved={isBookmarked}
              onToggle={handleBookmark}
            />
          </div>
        </div>
        
        {/* Exercise Metadata */}
        <div className="text-sm sm:text-base text-gray-600 mb-6 space-y-1">
          <p><span className="font-medium">Level:</span> {exercise.level}</p>
          <p><span className="font-medium">Equipment:</span> {exercise.equipment || "None"}</p>
          <p><span className="font-medium">Category:</span> {exercise.category}</p>
        </div>

        {/* Main Exercise Image with Crossfade */}
        {exercise.images && exercise.images.length > 0 && (
          <div className="relative mb-6">
            <div className="relative w-full h-64 sm:h-80 lg:h-96 rounded-lg overflow-hidden">
              {exercise.images.map((img, idx) => (
                img === "placeholder.jpg" ? (
                  <PlaceholderImage 
                    key={idx}
                    className={`absolute inset-0 w-full h-full transition-opacity duration-1000 ${
                      idx === currentImageIndex ? "opacity-100" : "opacity-0"
                    }`}
                  />
                ) : (
                  <img
                    key={idx}
                    src={`/exercise-images/${img}`}
                    alt={`${exercise.name} ${idx + 1}`}
                    className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${
                      idx === currentImageIndex ? "opacity-100" : "opacity-0"
                    }`}
                  />
                )
              ))}
            </div>
            
            {/* Image carousel dots */}
            {exercise.images.length > 1 && (
              <div className="flex justify-center mt-4 space-x-2">
                {exercise.images.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentImageIndex(idx)}
                    className={`w-2 h-2 rounded-full transition-colors duration-200 ${
                      idx === currentImageIndex ? "bg-gray-800" : "bg-gray-300"
                    }`}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Muscle Groups */}
        <div className="mb-6">
          <p className="text-gray-800 mb-2">
            <strong>Primary muscles:</strong> {exercise.primaryMuscles?.join(", ")}
          </p>
          <p className="text-gray-800">
            <strong>Secondary muscles:</strong> {exercise.secondaryMuscles?.join(", ")}
          </p>
        </div>

        {/* Suggested Sets & Reps */}
        {exercise.repRange && (
          <div className="mb-6 p-4 bg-gray-100 rounded-lg">
            <h2 className="font-semibold mb-3 text-gray-800">Suggested Sets & Reps</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {Object.entries(exercise.repRange).map(([goal, range]) => {
                const goalIcons = {
                  'strength': '💪',
                  'muscle_building': '🏋️',
                  'endurance': '🏃',
                  'power': '⚡',
                  'flexibility': '🧘',
                  'mobility': '🔄',
                  'recovery': '😌',
                  'cardio': '❤️',
                  'hiit': '🔥',
                  'conditioning': '⚡',
                  'explosive': '💥',
                  'technique': '🎯',
                  'stability': '⚖️',
                  'activation': '🔋'
                };
                
                return (
                  <div key={goal} className="bg-white p-3 rounded-lg border border-gray-200 hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-lg">{goalIcons[goal] || '💪'}</span>
                      <h3 className="font-medium text-sm text-gray-700 capitalize">
                        {goal.replace('_', ' ')}
                      </h3>
                    </div>
                    <p className="text-sm text-gray-600 font-mono">{range}</p>
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-gray-500 mt-3">
              💡 Choose the range that matches your training goal
            </p>
          </div>
        )}

        {/* Instructions */}
        {exercise.instructions && exercise.instructions.length > 0 && (
          <div className="mb-8">
            <h2 className="font-semibold mb-4">Instructions</h2>
            <ul className="list-disc pl-5 space-y-3">
              {exercise.instructions.map((step, idx) => (
                <li key={idx} className="text-gray-700 leading-relaxed">{step}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Exercise Progressions */}
        <ProgressionSection exercise={exercise} />

        {/* More Results Section */}
        <div className="border-t pt-8">
          <h2 className="text-xl font-bold text-gray-800 mb-6">More Exercises</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {relatedExercises.map((relatedExercise) => (
              <Link
                key={relatedExercise.id}
                to={`/exercise/${relatedExercise.id}`}
                className="block group"
              >
                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-shadow duration-200">
                  {/* Exercise Image */}
                  {relatedExercise.images && relatedExercise.images.length > 0 && (
                    <div className="relative h-32">
                      {relatedExercise.images[0] === "placeholder.jpg" ? (
                        <PlaceholderImage 
                          className="w-full h-full"
                        />
                      ) : (
                        <img
                          src={`/exercise-images/${relatedExercise.images[0]}`}
                          alt={relatedExercise.name}
                          className="w-full h-full object-cover"
                        />
                      )}
                    </div>
                  )}
                  
                  {/* Exercise Info */}
                  <div className="p-3">
                    <h3 className="font-semibold text-gray-800 mb-1 group-hover:text-blue-600 transition-colors">
                      {relatedExercise.name}
                    </h3>
                    <p className="text-sm text-gray-600 mb-2">
                      {relatedExercise.category} • {relatedExercise.level}
                    </p>
                    {relatedExercise.primaryMuscles && relatedExercise.primaryMuscles.length > 0 && (
                      <span className="inline-block px-2 py-1 bg-gray-200 text-gray-700 text-xs rounded-full">
                        {relatedExercise.primaryMuscles[0]}
                      </span>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
